import { CUSTOM_DATA_TYPES, type CustomAttribute, type CustomDataType } from '@shared/config';
import { Plus, Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Alert } from '../../components/ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../components/ui/form';

const TYPE_LABELS: Record<CustomDataType, string> = {
  text: 'Text',
  number: 'Number',
  boolean: 'Yes / No',
  url: 'URL',
  email: 'Email',
  phone: 'Phone',
  date: 'Date',
  list: 'List',
};

function slugify(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return slug || 'field';
}

export interface CustomAttributesProps {
  attributes: CustomAttribute[];
  onChange: (attributes: CustomAttribute[]) => void;
  error?: string | undefined;
}

/**
 * User-defined attributes described in plain language. `aiAssisted` decides whether AI may answer
 * when deterministic extraction finds nothing — results are labelled accordingly in the data.
 */
export function CustomAttributes({ attributes, onChange, error }: CustomAttributesProps) {
  const [draft, setDraft] = useState({ name: '', instruction: '', dataType: 'text' as CustomDataType, aiAssisted: true });

  const add = (): void => {
    const name = draft.name.trim();
    const instruction = draft.instruction.trim();
    if (!name || instruction.length < 3) return;
    const base = slugify(name);
    const taken = new Set(attributes.map((attribute) => attribute.key));
    let key = `custom.${base}`;
    let suffix = 2;
    while (taken.has(key)) key = `custom.${base}_${String(suffix++)}`;
    onChange([...attributes, { key, name, instruction, dataType: draft.dataType, aiAssisted: draft.aiAssisted }]);
    setDraft({ name: '', instruction: '', dataType: 'text', aiAssisted: true });
  };

  return (
    <div className="space-y-3">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {attributes.length > 0 ? (
        <ul className="space-y-2">
          {attributes.map((attribute, index) => (
            <li key={attribute.key} className="rounded-[var(--radius-panel)] border border-border bg-surface p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                    {attribute.name}
                    <span className="rounded border border-border px-1.5 py-0.5 text-[11px] font-normal text-muted-foreground">
                      {TYPE_LABELS[attribute.dataType]}
                    </span>
                    {attribute.aiAssisted ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Sparkles className="h-3 w-3" aria-hidden="true" /> AI allowed
                      </span>
                    ) : null}
                  </p>
                  <p className="mt-0.5 text-[13px] text-muted-foreground">{attribute.instruction}</p>
                </div>
                <button
                  type="button"
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label={`Remove ${attribute.name}`}
                  onClick={() => onChange(attributes.filter((_, position) => position !== index))}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="space-y-3 rounded-[var(--radius-panel)] border border-dashed border-border-strong p-3">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Field label="Attribute name">
            {({ id }) => (
              <Input
                id={id}
                value={draft.name}
                placeholder="Currently hiring"
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
              />
            )}
          </Field>
          <Field label="Answer type">
            {({ id }) => (
              <Select
                id={id}
                value={draft.dataType}
                onChange={(event) => setDraft((current) => ({ ...current, dataType: event.target.value as CustomDataType }))}
              >
                {CUSTOM_DATA_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Field label="What should it find?" hint="Describe it as you would to a researcher, for example: Does this company have open job postings right now?">
          {({ id, describedBy }) => (
            <Textarea
              id={id}
              value={draft.instruction}
              aria-describedby={describedBy}
              onChange={(event) => setDraft((current) => ({ ...current, instruction: event.target.value }))}
              placeholder="Does the company list open positions on its careers page?"
            />
          )}
        </Field>
        <Checkbox
          label="Let AI answer when the value cannot be extracted directly (answers are labelled as inferred)"
          checked={draft.aiAssisted}
          onChange={(event) => setDraft((current) => ({ ...current, aiAssisted: event.target.checked }))}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          icon={<Plus className="h-3.5 w-3.5" />}
          disabled={!draft.name.trim() || draft.instruction.trim().length < 3}
          onClick={add}
        >
          Add attribute
        </Button>
      </div>
    </div>
  );
}
